/**
 * Client-side PDF text extractor.
 *
 * Uses pdfjs-dist with dynamic import so it's only loaded when a PDF is
 * actually dropped. Handles the standard "empty user password" AES encryption
 * that the FedEx e-signature tool applies — pdfjs accepts that automatically.
 *
 * Returned text is stitched page-by-page with newlines. The worker's metadata
 * regex accepts it as-is; no layout reconstruction is attempted here.
 */

export interface PdfClientExtractResult {
  ok: boolean;
  text: string;
  page_count: number;
  error?: string;
}

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      // Worker URL — Vite serves the worker from node_modules via ?url.
      // Fall back to "fake worker" (main-thread) if the URL import fails.
      try {
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      } catch {
        // Main-thread fallback. Slower but self-contained.
      }
      return pdfjs;
    })();
  }
  return pdfjsPromise;
}

export async function extractPdfTextClient(file: File): Promise<PdfClientExtractResult> {
  try {
    const pdfjs = await loadPdfjs();
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf, password: '' }).promise;
    const pageCount = doc.numPages;
    const parts: string[] = [];
    for (let i = 1; i <= pageCount; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const items = content.items as Array<{ str?: string }>;
      const pageText = items.map((it) => it.str || '').join(' ');
      parts.push(pageText);
    }
    await doc.destroy();
    return { ok: true, text: parts.join('\n'), page_count: pageCount };
  } catch (err) {
    return {
      ok: false,
      text: '',
      page_count: 0,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
